/**
 * api 느려서 임시로 prefetching 합니다.
 * TODO: api 속도 개선후 제거해주세요.
 */
import { inject, onMounted, onUnmounted, watch } from 'vue';

import { QueryClient } from '@tanstack/vue-query';

import moment from 'moment';

import { Apis } from '@/constants/api';
import { ChartDataSchema, SettlementHeaderSchema, SummaryCustomersSchema, SummarySettlementSchema } from '@/schema/api/settlement.schema';
import { useRequestStore } from '@/store/useRequestStore';
import { wrapQueryPromise } from '@/utils/api';
import { localStoragePersiterHelper } from '@/utils/persist';

import { useGetSettlementDashboardHeaderData } from './settlement.service';

export const usePrefetchSettlementDashboardInit = () => {
  if (!inject('queryClient')) {
    console.error('queryClient is not provided');
  }

  const queryClient = inject<QueryClient>('queryClient') || new QueryClient();

  const { $get } = useRequestStore();

  // 기본값
  const date = moment();
  const type = 'monthly';
  const resource_group = '전체';

  const prefetch = () => {
    const settlementDashboardChartPersisterHelper = localStoragePersiterHelper('settlement_dashboard_chart', ChartDataSchema);
    queryClient.prefetchQuery({
      queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, date.format('YYYY-MM'), resource_group, type],
      queryFn: () => {
        return wrapQueryPromise(
          $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, {
            params: { type, resource_group: resource_group === '전체' ? '' : resource_group, date: date.format('YYYY-MM') },
          }),
          ChartDataSchema,
        ).then(
          settlementDashboardChartPersisterHelper.persistHandler([
            Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART,
            date.format('YYYY-MM'),
            resource_group,
            type,
          ]),
        );
      },
      persister: settlementDashboardChartPersisterHelper.persister,
    });

    const settlementDashboardSummaryPersisterHelper = localStoragePersiterHelper('settlement_dashboard_summary', SummarySettlementSchema);
    queryClient.prefetchQuery({
      queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, resource_group],
      queryFn: () =>
        wrapQueryPromise(
          $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, {
            params: { resource_group: resource_group === '전체' ? '' : resource_group },
          }),
          SummarySettlementSchema,
        ).then(
          settlementDashboardSummaryPersisterHelper.persistHandler([
            Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS,
            resource_group,
          ]),
        ),
      persister: settlementDashboardSummaryPersisterHelper.persister,
    });

    const settlementDashboardSummaryCustomersPersisterHelper = localStoragePersiterHelper(
      'settlement_dashboard_summary_customers',
      SummaryCustomersSchema,
    );
    queryClient.prefetchQuery({
      queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_CUSTOMERS, resource_group],
      queryFn: () =>
        wrapQueryPromise(
          $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_CUSTOMERS, {
            params: { resource_group: resource_group === '전체' ? '' : resource_group },
          }),
          SummaryCustomersSchema,
        ).then(
          settlementDashboardSummaryCustomersPersisterHelper.persistHandler([
            Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_CUSTOMERS,
            resource_group,
          ]),
        ),
      persister: settlementDashboardSummaryCustomersPersisterHelper.persister,
    });

    const settlementDashboardHeaderPersisterHelper = localStoragePersiterHelper('settlement_dashboard_header', SettlementHeaderSchema);
    queryClient.prefetchQuery({
      queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_HEAER] as string[],
      queryFn: () => {
        return wrapQueryPromise($get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_HEAER), SettlementHeaderSchema).then(
          settlementDashboardHeaderPersisterHelper.persistHandler([Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_HEAER]),
        );
      },
      persister: settlementDashboardHeaderPersisterHelper.persister,
    });
  };

  const onChangeVisibility = () => {
    if (document.visibilityState === 'visible') {
      prefetch();
    }
  };

  onMounted(() => {
    prefetch();
    window.addEventListener('visibilitychange', onChangeVisibility);
  });

  onUnmounted(() => {
    window.removeEventListener('visibilitychange', onChangeVisibility);
  });
};

export const usePrefetchSettlementDashboardAllChart = () => {
  if (!inject('queryClient')) {
    console.error('queryClient is not provided');
  }

  const queryClient = inject<QueryClient>('queryClient') || new QueryClient();

  const { $get } = useRequestStore();

  const { data } = useGetSettlementDashboardHeaderData();

  const prefetch = async (headerData: typeof data.value) => {
    if (!headerData) return;

    const startMoment = moment().set('year', 2024);
    const curMoment = moment();
    const dateList: string[] = [];
    while (startMoment.isSameOrBefore(curMoment, 'years')) {
      dateList.push(curMoment.format('YYYY-MM'));
      curMoment.add(-1, 'year');
    }

    const settlementDashboardChartPersisterHelper = localStoragePersiterHelper('settlement_dashboard_chart', ChartDataSchema);

    {
      // 화면 처음 보이는 요소만 먼저 가져오기 (같이 요청하는 경우 초기 로딩 속도 느려짐)
      const date = moment().format('YYYY-MM');
      const resource_group = '전체';
      await queryClient.prefetchQuery({
        queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, date, resource_group, 'monthly'],
        queryFn: () => {
          return wrapQueryPromise(
            $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, {
              params: { type: 'monthly', resource_group: resource_group === '전체' ? '' : resource_group, date: date },
            }),
            ChartDataSchema,
          ).then(
            settlementDashboardChartPersisterHelper.persistHandler([
              Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART,
              date,
              resource_group,
              'monthly',
            ]),
          );
        },
        persister: settlementDashboardChartPersisterHelper.persister,
      });
    }

    for (const resource_group of ['전체', ...headerData.resource_groups]) {
      for (const date of dateList) {
        queryClient.prefetchQuery({
          queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, date, resource_group, 'monthly'],
          queryFn: () => {
            return wrapQueryPromise(
              $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, {
                params: { type: 'monthly', resource_group: resource_group === '전체' ? '' : resource_group, date: date },
              }),
              ChartDataSchema,
            ).then(
              settlementDashboardChartPersisterHelper.persistHandler([
                Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART,
                date,
                resource_group,
                'monthly',
              ]),
            );
          },
          persister: settlementDashboardChartPersisterHelper.persister,
        });
      }

      const settlementDashboardSummaryPersisterHelper = localStoragePersiterHelper('settlement_dashboard_summary', SummarySettlementSchema);
      queryClient.prefetchQuery({
        queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, resource_group],
        queryFn: () =>
          wrapQueryPromise(
            $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, {
              params: { resource_group: resource_group === '전체' ? '' : resource_group },
            }),
            SummarySettlementSchema,
          ).then(
            settlementDashboardSummaryPersisterHelper.persistHandler([
              Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS,
              resource_group,
            ]),
          ),
        persister: settlementDashboardSummaryPersisterHelper.persister,
      });
    }
  };

  watch(
    data,
    headerData => {
      prefetch(headerData);
    },
    {
      immediate: true,
    },
  );

  const onChangeVisibility = () => {
    if (document.visibilityState === 'visible') {
      prefetch(data.value);
    }
  };

  onMounted(() => {
    window.addEventListener('visibilitychange', onChangeVisibility);
  });

  onUnmounted(() => {
    window.removeEventListener('visibilitychange', onChangeVisibility);
  });
};
